import { useState, useEffect } from 'react';
import useToast from '../contexts/useToast';

const Toast = () => {
  const { toast, hideToast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (toast) {
      setIsVisible(true);
      setIsLeaving(false);
    }
  }, [toast]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      hideToast();
      setIsVisible(false);
      setIsLeaving(false);
    }, 200);
  };

  if (!toast || !isVisible) return null;

  const config = {
    success: {
      accent: 'var(--teal)',
      bg: 'var(--teal-soft)',
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--teal)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
    },
    error: {
      accent: 'var(--accent)',
      bg: '#fdf6f5',
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      ),
    },
    info: {
      accent: 'var(--primary-soft)',
      bg: 'var(--background-alt)',
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--primary-soft)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      ),
    },
  };

  const { accent, bg, icon } = config[toast.type];

  return (
    <div
      style={{
        zIndex: 1000,
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: `translateX(-50%) translateY(${isLeaving ? '20px' : '0'})`,
        opacity: isLeaving ? 0 : 1,
        animation: isLeaving
          ? undefined
          : 'toastEnter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        transition: 'transform 0.2s ease, opacity 0.2s ease',
        minWidth: '280px',
        maxWidth: 'calc(100vw - 40px)',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: bg,
        borderRadius: '10px',
        padding: '12px 16px',
        boxShadow:
          '0 4px 20px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)',
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {icon}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: '0.9rem',
          fontWeight: 500,
          color: 'var(--primary)',
          lineHeight: 1.3,
        }}
      >
        {toast.message}
      </span>
      {toast.action && (
        <button
          onClick={() => {
            toast.action?.onClick();
            handleClose();
          }}
          style={{
            background: accent,
            color: 'white',
            border: 'none',
            fontSize: '0.8rem',
            cursor: 'pointer',
            padding: '6px 12px',
            borderRadius: '6px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          transition: 'background-color 0.15s ease',
          flexShrink: 0,
          marginLeft: '-4px',
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)')
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = 'transparent')
        }
        aria-label="Close toast"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--primary-soft)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <style>{`
        @keyframes toastEnter {
          0% {
            transform: translateX(-50%) translateY(30px) scale(0.95);
            opacity: 0;
          }
          100% {
            transform: translateX(-50%) translateY(0) scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Toast;
